import React, { useState, useEffect, useRef } from "react";
import { RCCBadge, RCCButton, RCCGrid, RCCIcon, RCCInput, RCCRangeSlider, RCCTextArea, RCCTooltip } from "react-common-components";
import Audit from "src/components/Audit";
import DateTimePicker from "src/components/DateTimePicker";
import LovCategoriesOccurrences from "src/components/Lov/CategoriesOccurrences";
import LovOriginOccurrences from "src/components/Lov/OriginOccurrences";
import LovRequirementsOccurrences from "src/components/Lov/RequirementsOccurrences";
import LovSectors from "src/components/Lov/Sectors";
import LovTags from "src/components/Lov/Tags";
import LovUsers from "src/components/Lov/Users";
import { useTranslation } from "src/context/Translations/context";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import BadgeTraceabilityBasic from "src/pages/Actions/Panel/components/BadgeTraceability/badgeTraceabilityBasic";
import { checkSituationOccurrence,  IRefModalHistoric, ITabOccurenceGeneral } from "../../../interface";
import ModalHistoric from "../../modalHistoric";

const TabGeneral : React.FC<ITabOccurenceGeneral> = ({
	occurrence,
	register,
	control,
	error, 
	watch,
	isAdd,
	isCopy,
	disabled,
	onChangeCategoryOccurence
}) => {

	const { translate } = useTranslation();

	const { user } = useAuth();

	const refModalHistoric = useRef<IRefModalHistoric>(null);
	const [ocb001Prioridade, setOcb001Prioridade] = useState(0);

	useEffectSkipInitialRender(() => {
		const selectedCategoryOccurenceId = parseInt(watch("ocb001Oca004Id"), 10) ;

		if(!selectedCategoryOccurenceId)
			return;

		if(onChangeCategoryOccurence && selectedCategoryOccurenceId)
			onChangeCategoryOccurence(selectedCategoryOccurenceId);
		else if(onChangeCategoryOccurence)
			onChangeCategoryOccurence(undefined);

	}, [watch("ocb001Data"), watch("ocb001Oca004Id")]);

	// Calcular Prioridade
	useEffect(() => {
		const ocb001Gravidade = watch("ocb001Gravidade");
		const ocb001Urgencia = watch("ocb001Urgencia");
		const ocb001Tendencia =  watch("ocb001Tendencia");
		setOcb001Prioridade(ocb001Gravidade * ocb001Urgencia * ocb001Tendencia);
	}, [watch("ocb001Gravidade"), watch("ocb001Urgencia"),watch("ocb001Tendencia")]);


	const defaultValue = (value: any, label: any) => {
		if(label && value)
			return { value: String(value), label};

		if(value)
			return value.toString();

		return "";
	};

	const colStatus = (row: any) => {

		let color: "blue" | "purple" | "red" | "green" | "grey" | "black";
		let text = "";
		switch(row.ocb001Status){
		case checkSituationOccurrence.NOT_STARTED:
			text = translate("Não iniciada");
			color = "blue";
			break;
		case checkSituationOccurrence.OPENED:
			text = translate("Em andamento");
			color = "purple";
			break;
		case checkSituationOccurrence.LATE:
			text = translate("Atrasado");
			color = "red";
			break;
		case checkSituationOccurrence.FINISHED:
			text = translate("Finalizado");
			color = "green";
			break;
		case checkSituationOccurrence.CANCELED:
			text = translate("Cancelado");
			color = "grey";
			break;
		case checkSituationOccurrence.DISAPPROVED:
			text = translate("Reprovado");
			color = "black";
			break;
		default: 
			color = "green";
			break;
		}

		return (
			<>
				<RCCBadge color={color} styleInline="padding: 2px 8px; font-size: 0.78rem;">{text}</RCCBadge>
			</>
		);
	};

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
				alignItems="center"
			>
				{!isAdd && (
					<RCCGrid
						container
						item
						alignItems="center"
						justify="flex-end"
					>
						<Title color="purple">
							{translate("Código Interno")}: {occurrence.ocb001CodigoInterno}
						</Title>
						{occurrence.ocb001Status !== checkSituationOccurrence.FINISHED && occurrence.ocb001Status !== checkSituationOccurrence.CANCELED && occurrence.ocb001Status !== checkSituationOccurrence.DISAPPROVED && (
							<>
								<Title color="purple">
									{translate("Etapa atual")}:&nbsp;{translate(`${occurrence.oca003Descricao}`)}
								</Title>
							</>
						)}
						{colStatus(occurrence)}
						{occurrence.ocb001Reincidente === "S" && (
							<>
								<Title color="purple">
									{translate("Reincidente")}:&nbsp;{occurrence.ocb001Reincidente}
								</Title>
								<RCCIcon name="infoCircle" style={{ marginLeft: "0.2rem", marginRight: "0.2rem"}} />
							</>
						)}						
						{occurrence.traceability && Object.keys(occurrence.traceability).length > 0 && (
							<BadgeTraceabilityBasic 
								layout="modal"
								traceability={occurrence.traceability}
							/>
						)}
						<RCCTooltip color="grey" text={translate("Histórico")}>
							<RCCButton color="grey" icon={{ name: "history", size: 13 }}
								onClick={() => {
									if(!refModalHistoric.current)
										return;
									refModalHistoric.current.actionButtonHistoric(occurrence);
								}}
							/>
						</RCCTooltip>
						<Audit data={occurrence} type="pop"/>
					</RCCGrid>
				)}
				<RCCGrid
					container
					item		
				>
					<RCCInput 
						name="ocb001Codigo"
						label={translate("Código")}
						maxLength={40}
						register={register}
						defaultValue={occurrence.ocb001Codigo}
						error={error.ocb001Codigo}
						disabled={disabled}
					/>
				</RCCGrid>

				<RCCGrid
					container
					item		
				>
					<RCCInput 
						name="ocb001Titulo"
						label={translate("Título")}
						maxLength={200}
						register={register}
						defaultValue={isCopy ? `(${translate("CÓPIA")}) ${occurrence.ocb001Titulo}` : occurrence.ocb001Titulo}
						error={error.ocb001Titulo}
						disabled={disabled}
						required
					/>
				</RCCGrid>
				<RCCGrid
					container
					item		
				>
					<RCCTextArea 
						name="ocb001Descricao"
						label={translate("Descrição")}
						maxLength={4000}	
						register={register}
						defaultValue={occurrence.ocb001Descricao}
						error={error.ocb001Descricao}
						disabled={disabled}
						required
					/>
				</RCCGrid>
				<RCCGrid
					container
					item		
				>
					<DateTimePicker 
						label={translate("Data")}
						name="ocb001Data"
						type="datePicker"
						control={control}
						defaultValue={occurrence.ocb001Data ?? new Date()}
						error={error.ocb001Data}
						props={{
							datePicker: {
								disabled
							}
						}}
					/>
				</RCCGrid>
				<RCCGrid
					container
					item		
				>
					<LovSectors 
						control={control}
						name="ocb001Taa001Id"
						label={translate("Setor")}
						defaultValue={defaultValue(occurrence.ocb001Taa001Id, occurrence.ocb001Taa001Descricao)}
						required
						disabled={disabled}
						isCreatable
						error={error.ocb001Taa001Id}
					/>
				</RCCGrid>
				<RCCGrid
					container
					item		
				>
					<RCCRangeSlider
						control={control}
						name="ocb001Gravidade"
						color="purple"
						label={translate("Gravidade")}
						defaultValue={occurrence.ocb001Gravidade}
						min={1}
						max={5}
						disabled={disabled}
						valueLabelDisplay="on"
						styleInline=".MuiSlider-valueLabel > span > span{ color: var(--input-font-color); }"
					/>
				</RCCGrid>
				<RCCGrid
					container
					item		
				>
					<RCCRangeSlider
						control={control}
						name="ocb001Urgencia"
						color="purple"
						label={translate("Urgência")}
						defaultValue={occurrence.ocb001Urgencia}
						min={1}
						max={5}
						disabled={disabled}
						valueLabelDisplay="on"
						styleInline=".MuiSlider-valueLabel > span > span{ color: var(--input-font-color); }"
					/>
				</RCCGrid>
				<RCCGrid
					container
					item		
				>
					<RCCRangeSlider
						control={control}
						name="ocb001Tendencia"
						color="purple"
						label={translate("Tendência")}
						defaultValue={occurrence.ocb001Tendencia}
						min={1}
						max={5}
						disabled={disabled}
						valueLabelDisplay="on"
						styleInline=".MuiSlider-valueLabel > span > span{ color: var(--input-font-color); }"
					/>
				</RCCGrid>
				<RCCGrid
					container
					item		
				>
					<RCCRangeSlider
						control={control}
						name="ocb001Prioridade"
						color="purple"
						label={translate("Prioridade (Calculada)")}
						value={ocb001Prioridade}
						min={1}
						max={125}
						valueLabelDisplay="on"
						disabled
						styleInline=".MuiSlider-valueLabel > span > span{ color: var(--input-font-color); }"
					/>
				</RCCGrid>
				<RCCGrid
					container
					item		
				>
					<LovUsers 
						control={control}
						name="ocb001Efa005Id"
						label={translate("Operador")}
						error={error.ocb001Efa005Id}
						defaultValue={occurrence.ocb001Efa005Id ? defaultValue(occurrence.ocb001Efa005Id, occurrence.ocb001Efa005Nome) : {
							value: user.efa005Id.toString(),
							label: user.efa005Nome
						}}
						disabled={disabled}
						required
					/> 
				</RCCGrid>
				<RCCGrid
					container
					item		
				>
					<LovRequirementsOccurrences 
						control={control}
						name="ocb001Oca001Id"
						label={translate("Requisito")}
						error={error.ocb001Oca001Id}
						defaultValue={defaultValue(occurrence.ocb001Oca001Id, occurrence.oca001Descricao)}
						disabled={disabled}
						required
					/>
				</RCCGrid>
				<RCCGrid
					container
					item		
				>
					<LovOriginOccurrences 
						control={control}
						name="ocb001Oca002Id"
						error={error.ocb001Oca002Id}
						label={translate("Origem")}
						defaultValue={defaultValue(occurrence.ocb001Oca002Id, occurrence.oca002Descricao)}
						disabled={disabled || (!isCopy && occurrence.ocb001Status === checkSituationOccurrence.OPENED)}
						required
					/>
				</RCCGrid>
				<RCCGrid
					container
					item		
				>
					<LovCategoriesOccurrences 
						control={control}
						name="ocb001Oca004Id"
						label={translate("Categoria")}
						error={error.ocb001Oca004Id}
						defaultValue={defaultValue(occurrence.ocb001Oca004Id, occurrence.oca004Descricao)}
						disabled={disabled || (!isCopy && occurrence.ocb001Status === checkSituationOccurrence.OPENED)}
						required
					/>
				</RCCGrid>

				<RCCGrid
					container
					item		
				>
					<LovTags 
						control={control}
						name="ocb001Tags"
						label={translate("Tags")}
						defaultValue={Array.isArray(occurrence.ocb001Tags) ? occurrence.ocb001Tags.join(", ") : occurrence.ocb001Tags}
						disabled={disabled}
						extraParametersOnLoad={{
							use: 4
						}}
						extraParametersOnCreate={{
							use: 4
						}}
					/>
				</RCCGrid>
			</RCCGrid>
			<ModalHistoric ref={refModalHistoric} />
		</>
	);
};

export default TabGeneral;
